import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  ArrowRight,
  CheckCircle2,
  Users,
  GraduationCap,
  Briefcase,
  Globe2,
  ShieldCheck,
  Mail,
  Phone,
  Building2,
  Award,
  BadgeCheck,
  Lightbulb,
  Layers,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

type AudienceType = "high-school" | "university" | "executive";

interface ApiResponse {
  success?: boolean;
  message?: string;
  errors?: { [key: string]: string | string[] };
}

const ExecutiveCertificates: React.FC = () => {
  const [isBrochureOpen, setIsBrochureOpen] = useState(false);
  const [brochureDownloaded, setBrochureDownloaded] = useState(false);
  const [audienceTab, setAudienceTab] = useState<AudienceType>("high-school");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    institution: "",
    audience: "high-school" as AudienceType,
    consent: false,
  });
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scheduleDay, setScheduleDay] = useState<1 | 2>(1);
  const [expandedFAQ, setExpandedFAQ] = useState<number>(0);
  const brochureUrl = useMemo(() => "/documents/Program-details.pdf", []);

  // Map frontend audience to backend role format
  const mapAudienceToRole = (audience: AudienceType): string => {
    const roleMapping: Record<AudienceType, string> = {
      "high-school": "High school educator / coordinator",
      university: "University staff / student",
      executive: "Executive / HR / L&D",
    };
    return roleMapping[audience];
  };

  const splitName = (
    fullName: string,
  ): { firstName: string; lastName: string } => {
    const trimmedName = fullName.trim().replace(/\s+/g, " ");
    const nameParts = trimmedName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ");
    return { firstName, lastName };
  };

  const triggerBrochureDownload = () => {
    const link = document.createElement("a");
    link.href = brochureUrl;
    link.download = "NYEA-Program-Details.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateForm = () => {
    const errors: { [k: string]: string } = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    else {
      const { firstName, lastName } = splitName(formData.name);
      if (!firstName || !lastName)
        errors.name = "Please enter both first and last name";
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Valid email is required";
    if (!formData.consent) errors.consent = "Please agree to be contacted";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({}); // Clear previous errors

    try {
      const { firstName, lastName } = splitName(formData.name);

      // Prepare payload for backend API
      const apiPayload = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        institution: formData.institution.trim() || null,
        role: mapAudienceToRole(formData.audience),
      };

      // Construct API URL - FIXED: Don't duplicate /api/v1
      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_API_URL ||
        "http://localhost:8000/api/v1";
      const apiUrl = `${API_BASE_URL}/marketing-contacts`; // ✅ Correct!

      if (
        typeof window !== "undefined" &&
        window.location?.hostname === "localhost"
      ) {
        console.log("[Brochure form] POST", apiUrl);
        console.log("[Brochure form] Payload", apiPayload);
      }

      // Send to backend API
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(apiPayload),
      });

      // Safely parse JSON response
      let result: ApiResponse = {};
      try {
        const text = await response.text();
        result = text ? (JSON.parse(text) as ApiResponse) : {};
      } catch (parseError) {
        if (typeof window !== "undefined")
          console.error(
            "[Brochure form] Invalid JSON response",
            response.status,
            parseError,
          );
        setFormErrors({
          submit: "Invalid response from server. Please try again.",
        });
        triggerBrochureDownload();
        return;
      }

      if (
        typeof window !== "undefined" &&
        window.location?.hostname === "localhost"
      ) {
        console.log("[Brochure form] Response", response.status, result);
      }

      if (!response.ok) {
        // Handle validation errors from backend
        if (result.errors) {
          const backendErrors: { [k: string]: string } = {};
          Object.keys(result.errors).forEach((key) => {
            const errorValue = result.errors![key];
            const message = Array.isArray(errorValue)
              ? errorValue[0]
              : errorValue;
            if (key === "first_name" || key === "last_name") {
              backendErrors.name = backendErrors.name || message;
            } else {
              backendErrors[key] = message;
            }
          });
          setFormErrors(backendErrors);
        } else {
          setFormErrors({
            submit: result.message || `Server error: ${response.status}`,
          });
        }
        triggerBrochureDownload();
        return;
      }

      // Success - Store locally for tracking
      const leadPayload = {
        ...formData,
        source: "Executive Certificate Series",
        createdAt: new Date().toISOString(),
        submittedToBackend: true,
      };
      localStorage.setItem(
        "nyea_exec_series_lead",
        JSON.stringify(leadPayload),
      );

      // Mark as downloaded
      setBrochureDownloaded(true);

      // Trigger brochure download
      triggerBrochureDownload();

      // Close modal after successful submission
      setTimeout(() => {
        setIsBrochureOpen(false);
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          institution: "",
          audience: "high-school" as AudienceType,
          consent: false,
        });
        setFormErrors({});
        setBrochureDownloaded(false);
      }, 1500);
    } catch (error: any) {
      if (typeof window !== "undefined")
        console.error("[Brochure form] Error", error);

      // Show user-friendly error message
      let errorMessage =
        "Unable to submit form. Please try again or contact us directly.";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.name === "TypeError") {
        errorMessage =
          "Cannot connect to server. Please check if backend is running.";
      }

      setFormErrors({
        submit: errorMessage,
      });
      triggerBrochureDownload();
    } finally {
      setIsSubmitting(false);
    }
  };

  const workshops = [
    {
      slug: "ai-communication",
      title: "AI-Powered Communication & Innovation",
      excerpt:
        "Use AI to amplify persuasive communication, creativity, and innovation workflows.",
      gradient: "from-indigo-500 to-purple-600",
      duration: "October 2025",
      audience: "University",
      topics: ["AI Tools", "Communication", "Innovation"],
      completed: true,
    },
    {
      slug: "entrepreneurship",
      title: "Entrepreneurship & Startup Strategy",
      excerpt:
        "From idea to MVP: customer discovery, business models, and go-to-market planning.",
      gradient: "from-orange-500 to-red-600",
      duration: "October 2025",
      audience: "Executive",
      topics: ["Business Models", "MVP Development", "Market Strategy"],
      completed: true,
    },
    {
      slug: "leadership-negotiation-communication",
      title: "Leadership, Negotiation and Communication Skills",
      excerpt:
        "Master conflict resolution, organizational leadership, and effective negotiation techniques with cutting-edge methods.",
      gradient: "from-emerald-500 to-teal-600",
      instructor: "Ivy League Faculty Team",
      institution: "Ivy League",
      audience: "High School",
      duration: "December 2025",
      topics: ["Leadership", "Negotiation", "Conflict Resolution"],
      featured: true,
    },
  ];

  const benefits = [
    {
      icon: Award,
      title: "Taught by Top University Professionals",
      desc: "Live sessions delivered by professionals and senior practitioners affiliated with institutions such as Ivy League, Stanford, and Cambridge. Content is refreshed each cohort to reflect the latest research and practice.",
    },
    {
      icon: Lightbulb,
      title: "Real-World, Portfolio-Ready Output",
      desc: "Participants build tangible deliverables such as strategy memos, AI-enhanced presentations, and venture blueprints that elevate applications and professional profiles.",
    },
    {
      icon: BadgeCheck,
      title: "Professional Certificates",
      desc: "Verified learning outcomes and professionally recognized certificates for high school, university, and executive audiences.",
    },
    {
      icon: Users,
      title: "Confidence and Communication",
      desc: "Guided practice, peer feedback, and professor coaching expand communication range, executive presence, and strategic thinking.",
    },
    {
      icon: Globe2,
      title: "Global Cohorts and Networks",
      desc: "Learn alongside peers from 20+ countries. Build a global network of ambitious students and leaders.",
    },
    {
      icon: Layers,
      title: "Flexible Delivery",
      desc: "2-day accredited format with modular add-ons. We tailor scope and depth by audience proficiency and institutional goals.",
    },
  ];

  const scheduleData = {
    1: [
      {
        title: "Leadership Foundations",
        subtitle:
          "Discover your unique leadership style and build your profile",
        type: "foundation",
      },
      {
        title: "Leadership in Action",
        subtitle:
          "What makes a great leader? Interactive exploration and assessment",
        type: "interactive",
      },
      {
        title: "Communication Mastery",
        subtitle: "Develop your unique vocabulary and speaking presence",
        type: "communication",
      },
      {
        title: "Speaking Like a Leader",
        subtitle: "Group work and peer feedback on leadership communication",
        type: "group",
      },
    ],
    2: [
      {
        title: "Identity & Emotional Intelligence",
        subtitle: "Culture and gender dynamics in effective communication",
        type: "foundation",
      },
      {
        title: "Case Study Deep Dive",
        subtitle: "Real-world simulations and strategic analysis",
        type: "case",
      },
      {
        title: "Negotiation Mastery",
        subtitle:
          "Categories, zones of flexibility, and Ivy League-style techniques",
        type: "negotiation",
      },
      {
        title: "Global Trade Simulation",
        subtitle: "Creative resolutions for Country X vs Y trade conflict",
        type: "simulation",
      },
    ],
  };

  const schedule = scheduleData[scheduleDay] || scheduleData[1];

  const testimonials = [
    {
      name: "Maria Gomez",
      role: "Head of Sixth Form, Madrid",
      content:
        "Our top set students left with sharper decision frameworks and stronger application narratives. The faculty quality was clear from minute one.",
    },
    {
      name: "Omar Khan",
      role: "Dean, Lahore",
      content:
        "The workshops balanced academic rigor with practical application. We saw immediate uplift in student confidence and communication.",
    },
    {
      name: "Ethan Lee",
      role: "Executive Participant, Seoul",
      content:
        "We applied the strategy tools to a real initiative the next day. Clear, modern, and outcome-focused.",
    },
  ];

  const universityLogos = [
    { name: "Ivy League", url: "/images/stanford-logo.png" },
    { name: "Stanford University", url: "/images/harvard2.png" },
    { name: "University of Cambridge", url: "/images/cambridge2.png" },
    { name: "USA Education", url: "/images/usaedu.png" },
  ];

  const certifications = [
    {
      name: "ISO 9001:2015",
      description: "Quality Management System",
      badge: true,
    },
    {
      name: "Professional Certification",
      description: "Industry Standards",
      badge: true,
    },
  ];

  const faq = [
    {
      q: "Who delivers the workshops?",
      a: "Sessions are designed and taught by professors and senior practitioners affiliated with leading universities (e.g., Ivy League, Stanford, Cambridge). All content follows professional standards and is updated per cohort.",
    },
    {
      q: "What accreditation do participants receive?",
      a: "Participants receive professional certificates upon successful completion. Institutions can request customized assessment rubrics aligned to internal outcomes.",
    },
    {
      q: "Who is eligible?",
      a: "High school (advanced English), university students, and executives. We tailor difficulty, pace, and casework by segment. Pre-reads and suggested skill primers are provided.",
    },
    {
      q: "How soon can we run one?",
      a: "From inquiry to delivery is typically ~6 weeks, including alignment, scheduling, and content finalization. Expedited timelines are possible depending on availability.",
    },
    {
      q: "Do you offer custom content?",
      a: "Yes. We tailor by audience, industry focus, and measurable outcomes. Options include extended projects, capstones, and faculty advising hours.",
    },
    {
      q: "Global delivery?",
      a: "Yes. In-person or virtual delivery in 20+ countries, with time-zone coverage and bilingual facilitators available on request.",
    },
    {
      q: "Data and privacy?",
      a: "We comply with institutional data policies, ensuring secure handling of participant data and optional NDA coverage for executive cohorts.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1f2444] via-[#2d3d66] to-[#9f162e]">
        {/* Modern Hero Background Image */}
        <div className="absolute inset-0">
          <div
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"%3E%3Cdefs%3E%3ClinearGradient id="bg" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%23ffffff;stop-opacity:0.05"/%3E%3Cstop offset="50%25" style="stop-color:%23ffffff;stop-opacity:0.02"/%3E%3Cstop offset="100%25" style="stop-color:%23ffffff;stop-opacity:0.05"/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="1200" height="800" fill="url(%23bg)"/%3E%3Cg opacity="0.3"%3E%3Ccircle cx="200" cy="150" r="3" fill="%23ffffff"/%3E%3Ccircle cx="400" cy="250" r="2" fill="%23ffffff"/%3E%3Ccircle cx="600" cy="180" r="4" fill="%23ffffff"/%3E%3Ccircle cx="800" cy="300" r="2" fill="%23ffffff"/%3E%3Ccircle cx="1000" cy="200" r="3" fill="%23ffffff"/%3E%3Ccircle cx="300" cy="400" r="2" fill="%23ffffff"/%3E%3Ccircle cx="700" cy="450" r="3" fill="%23ffffff"/%3E%3Ccircle cx="150" cy="500" r="2" fill="%23ffffff"/%3E%3Ccircle cx="950" cy="550" r="4" fill="%23ffffff"/%3E%3Ccircle cx="500" cy="600" r="2" fill="%23ffffff"/%3E%3C/g%3E%3C/svg%3E')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1f2444]/85 via-[#2d3d66]/75 to-[#9f162e]/80" />
        </div>
        {/* Modern geometric elements */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-[#9f162e]/15 blur-3xl rounded-full" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 w-[28rem] h-[28rem] bg-white/10 blur-3xl rounded-full" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-white/30 rotate-45 rounded-lg"></div>
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-white/20 rotate-12 rounded-full"></div>
          <div className="absolute top-1/2 right-1/3 w-16 h-16 border border-white/25 rotate-45 rounded-lg"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center px-5 py-2 rounded-full bg-gradient-to-r from-[#9f162e]/20 to-[#1f2444]/30 backdrop-blur-md border border-white/30 text-white text-sm mb-6 font-medium">
              ELITE WORKSHOP SERIES
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight max-w-5xl">
              <span className="bg-gradient-to-r from-white via-gray-100 to-orange-200 bg-clip-text text-transparent">
                Transform Profiles. Accelerate Futures.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mt-6 max-w-3xl">
              High-impact workshops with professional certification and
              international delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Button
                size="lg"
                className="bg-white text-[#1f2444] hover:bg-gray-100 shadow-lg font-semibold"
                onClick={() => setIsBrochureOpen(true)}
              >
                <div className="flex items-center">
                  Download Brochure
                  <Download className="ml-2 h-5 w-5" />
                </div>
              </Button>

              {/* Magic Button for Book Discovery Call */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#9f162e] via-orange-400 to-[#1f2444] rounded-xl blur opacity-75 group-hover:opacity-100 transition duration-300 group-hover:duration-200 animate-pulse"></div>
                <Link
                  to="/contact"
                  className="relative inline-flex items-center justify-center h-10 md:h-12 lg:h-14 rounded-xl px-8 text-xs md:text-sm lg:text-md font-semibold bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1f2444] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  Book Discovery Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-gray-200/90">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2" /> Students & Executives
              </div>
              <div className="flex items-center">
                <ShieldCheck className="h-5 w-5 mr-2" /> Professional
                Certificate
              </div>
              <div className="flex items-center">
                <Globe2 className="h-5 w-5 mr-2" /> Delivered in 20+ Countries
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Workshop Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900">
              Top Workshops
            </h2>
            <p className="text-gray-600 mt-4">
              Explore our most in-demand workshops. Details are on separate
              pages.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {workshops.map((w, i) => (
              <motion.div
                key={w.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={w.featured ? "md:scale-105 md:z-10" : ""}
              >
                <div
                  className={`relative bg-gradient-to-r ${w.gradient} rounded-2xl p-[1px] ${w.featured ? "shadow-2xl" : "shadow-lg"}`}
                >
                  <Card
                    className={`h-full bg-white rounded-2xl overflow-hidden ${w.featured ? "border-2 border-yellow-400" : ""}`}
                  >
                    {w.featured && (
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-center py-2 px-4">
                        <div className="text-xs font-bold tracking-wide">
                          ⭐ Registrations Open
                        </div>
                      </div>
                    )}
                    {w.completed && (
                      <div className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-center py-2 px-4">
                        <div className="text-xs font-bold tracking-wide">
                          Registration Closed
                        </div>
                      </div>
                    )}
                    <CardHeader className="pb-4">
                      <div className="flex justify-between items-start mb-2">
                        <div
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            w.audience === "High School"
                              ? "bg-blue-100 text-blue-700"
                              : w.audience === "University"
                                ? "bg-purple-100 text-purple-700"
                                : w.audience === "Executive"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {w.audience}
                        </div>
                        <div className="text-xs text-gray-500 font-medium">
                          {w.duration}
                        </div>
                      </div>
                      <CardTitle
                        className={`font-bold leading-tight ${w.featured ? "text-xl" : "text-lg"}`}
                      >
                        {w.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {w.instructor && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4">
                          <div className="text-center">
                            <div className="text-sm font-bold text-gray-900">
                              By {w.instructor}
                            </div>
                            <div className="text-xs text-gray-600">
                              {w.institution}
                            </div>
                            <div className="text-xs text-blue-600 mt-1">
                              for New York Empire Academy participants
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm mb-2">
                            What You'll Learn:
                          </h4>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {w.topics.map((topic, idx) => (
                              <span
                                key={idx}
                                className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {w.excerpt}
                        </p>
                      </div>
                      {!w.completed && (
                        <Link
                          to={`/elite-workshops/${w.slug}`}
                          className={`w-full mt-6 border-0 ${
                            w.featured
                              ? "bg-gradient-to-r from-[#9f162e] to-orange-600 hover:from-orange-600 hover:to-[#9f162e]"
                              : "bg-gradient-to-r from-[#1f2444] to-[#9f162e] hover:from-[#9f162e] hover:to-[#1f2444]"
                          } text-white font-semibold flex items-center justify-center px-4 py-2 rounded-md transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]`}
                        >
                          {w.featured ? "View & Register" : "Learn More"}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Professors Spotlight */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
              Senior Professionals From Leading Universities
            </h3>
            <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
              We collaborate with professors and senior practitioners affiliated
              with institutions such as Ivy League, Stanford, and Cambridge.
            </p>
          </div>
          <div className="mb-8">
            {/* Instructor section hidden */}
          </div>
          <div className="mt-12 text-center">
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 max-w-6xl mx-auto">
              {universityLogos.map((l, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 flex items-center justify-center bg-white/5 rounded-lg p-2"
                >
                  <img
                    src={l.url}
                    alt={`${l.name} logo`}
                    className="max-w-full max-h-full object-contain opacity-80"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              Key Benefits
            </h3>
            <p className="text-blue-200 mt-3 max-w-3xl mx-auto">
              Modern, outcomes-driven learning designed to elevate applications
              and performance
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white flex items-center justify-center mr-4">
                    <b.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">
                      {b.title}
                    </div>
                    <p className="text-blue-200 mt-2 text-sm leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Partners */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ideal Partners
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Do your students aim for top universities?
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {(["high-school", "university", "executive"] as const).map(
              (key) => (
                <button
                  key={key}
                  onClick={() => setAudienceTab(key)}
                  className={`inline-flex items-center px-6 py-3 rounded-xl font-semibold transition-all ${
                    audienceTab === key
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {key === "high-school" && (
                    <GraduationCap className="h-5 w-5 mr-2" />
                  )}
                  {key === "university" && (
                    <Building2 className="h-5 w-5 mr-2" />
                  )}
                  {key === "executive" && (
                    <Briefcase className="h-5 w-5 mr-2" />
                  )}
                  {key === "high-school"
                    ? "High Schools"
                    : key === "university"
                      ? "Universities"
                      : "Executives"}
                </button>
              ),
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl bg-white p-8">
              <CardContent className="p-0">
                {audienceTab === "high-school" && (
                  <p className="text-gray-700 leading-relaxed">
                    Advanced English proficiency recommended. Perfect for
                    profile-building, leadership, and university applications.
                  </p>
                )}
                {audienceTab === "university" && (
                  <p className="text-gray-700 leading-relaxed">
                    Enhance employability with strategic, AI, and venture
                    skills; ideal for honors cohorts and career centers.
                  </p>
                )}
                {audienceTab === "executive" && (
                  <p className="text-gray-700 leading-relaxed">
                    High-impact leadership development with executive presence,
                    strategic communication, and organizational transformation
                    skills.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
              Ambition, Energy, Momentum
            </h3>
            <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
              A lively, achievement-driven atmosphere.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative rounded-2xl overflow-hidden shadow-md">
              <img
                src="/images/6.png"
                alt="Graduates"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-md md:col-span-2">
              <img
                src="/images/3.png"
                alt="Faculty coaching"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
              How It Works
            </h3>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto">
              A sophisticated journey from concept to transformational delivery
            </p>
          </div>
          <div className="text-center mt-10">
            <Button
              size="lg"
              onClick={() => setIsBrochureOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-semibold"
            >
              <Download className="w-5 h-5 mr-2" />
              Get the Complete Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Sample 2-Day Schedule */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Sample 2-Day Schedule
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Flexible format designed to maximize learning outcomes
            </p>
          </div>
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-2xl p-2 shadow-lg border border-gray-200">
              {([1, 2] as const).map((day) => (
                <button
                  key={day}
                  onClick={() => setScheduleDay(day)}
                  className={`px-8 py-3 rounded-xl font-semibold transition-all ${scheduleDay === day ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg" : "text-gray-600 hover:bg-blue-50"}`}
                >
                  Day {day}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-4">
              {schedule.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-600">{item.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-16">
            <Button
              size="lg"
              onClick={() => setIsBrochureOpen(true)}
              className="bg-white text-slate-900 hover:bg-gray-50 px-8 py-4 rounded-xl font-semibold"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Full Schedule
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
              What Partners Say
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card
                key={i}
                className="bg-white rounded-2xl border border-gray-100"
              >
                <CardContent className="p-6">
                  <p className="text-gray-700 leading-relaxed">"{t.content}"</p>
                  <div className="mt-4 font-semibold text-gray-900">
                    {t.name}
                  </div>
                  <div className="text-gray-600 text-sm">{t.role}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Frequently Asked Questions
              </h3>
            </div>
            <div className="space-y-4">
              {faq.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setExpandedFAQ(expandedFAQ === idx ? -1 : idx)
                    }
                    className="w-full text-left px-8 py-6 flex items-center justify-between"
                  >
                    <span className="text-white font-semibold text-lg">
                      {item.q}
                    </span>
                    <span className="text-blue-300 text-2xl">
                      {expandedFAQ === idx ? "−" : "+"}
                    </span>
                  </button>
                  {expandedFAQ === idx && (
                    <div className="px-8 pb-6">
                      <p className="text-blue-200 leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Lead Capture */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white">
            Get the full workshop guide
          </h3>
          <p className="text-blue-200 mt-2">
            Download the brochure or book a discovery call
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Button
              size="lg"
              className="bg-white text-slate-900 hover:bg-gray-100 font-semibold"
              onClick={() => setIsBrochureOpen(true)}
            >
              Download Brochure
            </Button>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-12 rounded-xl px-8 font-semibold border-2 border-white text-white bg-transparent hover:border-white/80 transition-all"
            >
              Book Discovery Call
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brochure Modal (Gated) */}
      <AnimatePresence>
        {isBrochureOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden"
              initial={{ scale: 0.95, y: 12, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 12, opacity: 0 }}
            >
              <div className="p-6 border-b">
                <h4 className="text-xl font-bold">
                  Access the Elite Workshop Series Brochure
                </h4>
                <p className="text-gray-600 mt-1">
                  Enter your details to download the full guide. We'll save your
                  information and be in touch.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Full name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className={`w-full rounded-lg border px-3 py-2 ${formErrors.name ? "border-red-500" : "border-gray-300"}`}
                      placeholder="Jane Doe"
                    />
                    <UserIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                  {formErrors.name && (
                    <p className="text-red-600 text-sm mt-1">
                      {formErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className={`w-full rounded-lg border px-3 py-2 ${formErrors.email ? "border-red-500" : "border-gray-300"}`}
                      placeholder="jane@example.com"
                    />
                    <Mail className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                  {formErrors.email && (
                    <p className="text-red-600 text-sm mt-1">
                      {formErrors.email}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Phone (optional)
                    </label>
                    <div className="mt-1 relative">
                      <input
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                        placeholder="+1 555 123 4567"
                      />
                      <Phone className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Institution (optional)
                    </label>
                    <input
                      value={formData.institution}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          institution: e.target.value,
                        })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="School / University"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    I am a
                  </label>
                  <select
                    value={formData.audience}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        audience: e.target.value as AudienceType,
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  >
                    <option value="high-school">
                      High school educator / coordinator
                    </option>
                    <option value="university">
                      University staff / student
                    </option>
                    <option value="executive">Executive / HR / L&D</option>
                  </select>
                </div>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) =>
                      setFormData({ ...formData, consent: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to be contacted about this brochure and related
                    programs.
                  </span>
                </label>
                {formErrors.consent && (
                  <p className="text-red-600 text-sm">{formErrors.consent}</p>
                )}
                {formErrors.submit && (
                  <p className="text-red-600 text-sm">{formErrors.submit}</p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsBrochureOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0"
                  >
                    {isSubmitting ? "Downloading…" : "Download brochure"}
                  </Button>
                </div>
                {brochureDownloaded && (
                  <div className="flex items-center gap-2 text-green-700 text-sm mt-2 bg-green-50 p-3 rounded-lg">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>
                      Success! Brochure is downloading. We'll contact you
                      shortly.
                    </span>
                  </div>
                )}
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 21a8 8 0 10-16 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 11a4 4 0 100-8 4 4 0 000 8z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const QuoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 7h4v10H5V9a2 2 0 012-2zm10 0h4v10h-6V9a2 2 0 012-2z"
      fill="currentColor"
    />
  </svg>
);

export default ExecutiveCertificates;
